import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import {Spin, Typography, Card, Row, Col, Divider, Tag, Button} from 'antd';
import { TagOutlined } from '@ant-design/icons';
import api from '../../api';
import { DEFAULT_GUTTER } from '../../constants';
import dayjs from 'dayjs';
import Footer from '../../Components/Footer';
import SEOHead from '../../Components/SEOHead';
import CallToAction from '../../Components/CallToAction';
import { getLanguageFromPath } from '../../i18n/config';
import './style.css';
import {useTranslations} from "../../lang";
import {linkProductsInHtml} from '../../productLinks';

const { Title, Text } = Typography;

export default function BlogPost() {
    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [randomBlogPosts, setRandomBlogPosts] = useState([])
    const [products, setProducts] = useState([])

    const {language} = useTranslations()

    useEffect(() => {
        if (slug) {
            Promise.all([
                api.getBlogBySlug(slug),
                api.products({}).catch(() => []),
            ]).then(([blogItem, productItems]) => {
                setBlog(blogItem)
                setProducts(productItems)
                api.getAllBlogs(language).then(setRandomBlogPosts)
            }).finally(() => {
                setLoading(false)
            })
        }
    }, [slug, language]);

    const linkedContent = useMemo(
        () => linkProductsInHtml(blog?.content || '', products),
        [blog, products]
    );



    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!blog) {
        return (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
                <Title level={2}>Blog post niet gevonden</Title>
                <Text>De blog post die je zoekt bestaat niet of is verwijderd.</Text>
            </div>
        );
    }

    return (
        <>
        {blog && (
            <SEOHead
                title={blog.title}
                description={blog.meta_description}
                keywords={blog.meta_keywords}
                ogImage={blog.image}
                ogImageAlt={blog.title}
            />
        )}
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem' }}>
            <Row gutter={DEFAULT_GUTTER}>
                <Col xs={24} lg={17}>
                    <Card className="blog-post-card">
                        {/* Blog Header Image */}
                        {blog.image && (
                            <div style={{ marginBottom: '2rem' }}>
                                <img
                                    src={blog.image}
                                    alt={blog.title}
                                    style={{
                                        width: '100%',
                                        borderRadius: '12px',
                                        maxHeight: '400px',
                                        objectFit: 'cover'
                                    }}
                                />
                            </div>
                        )}

                        {/* Blog Title */}
                        <Title level={1} style={{ marginBottom: '1rem' }}>
                            {blog.title}
                        </Title>

                        {/* Blog Meta Description */}
                        {blog.meta_description && (
                            <div style={{
                                marginBottom: '2rem',
                                padding: '1rem',
                                background: '#f6f6f6',
                                borderRadius: '8px',
                                fontStyle: 'italic'
                            }}>
                                <Text style={{ fontSize: '1.1rem' }}>
                                    {blog.meta_description}
                                </Text>
                            </div>
                        )}

                        {/* Blog Tags */}
                        {blog.tags && blog.tags.length > 0 && (
                            <div style={{ marginBottom: '2rem' }}>
                                <Text strong style={{ marginRight: '0.5rem' }}>
                                    <TagOutlined style={{ marginRight: '0.5rem' }} />
                                    Tags:
                                </Text>
                                {blog.tags.map((tagItem) => (
                                    <Link
                                        key={tagItem.id}
                                        to={`/blog/tag/${tagItem.tag}`}
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <Tag
                                            color="green"
                                            style={{
                                                fontSize: '1rem',
                                                padding: '0.25rem 0.75rem',
                                                marginBottom: '0.5rem',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {tagItem.tag}
                                        </Tag>
                                    </Link>
                                ))}
                            </div>
                        )}

                        <Divider />

                        {/* Blog Content - Rendered as HTML */}
                        <div
                            className="blog-content"
                            dangerouslySetInnerHTML={{ __html: linkedContent }}
                        />
                    </Card>
                </Col>

                {/* Sidebar with related blog posts */}
                <Col xs={24} lg={7}>
                    <Card
                        title="Meer artikelen"
                        style={{ position: 'sticky', top: '2rem' }}
                    >
                        {randomBlogPosts.map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={`/blog/${relatedBlog.slug}`}
                                style={{ textDecoration: 'none' }}
                            >
                                <div style={{
                                    marginBottom: '1.5rem',
                                    paddingBottom: '1.5rem',
                                    borderBottom: '1px solid #f0f0f0'
                                }}>
                                    {relatedBlog.image && (
                                        <img
                                            src={relatedBlog.image}
                                            alt={relatedBlog.title}
                                            style={{
                                                width: '100%',
                                                height: '150px',
                                                objectFit: 'cover',
                                                borderRadius: '8px',
                                                marginBottom: '0.75rem'
                                            }}
                                        />
                                    )}
                                    <Title
                                        level={5}
                                        style={{
                                            marginBottom: '0.5rem',
                                            color: '#006e2f'
                                        }}
                                    >
                                        {relatedBlog.title}
                                    </Title>
                                </div>
                            </Link>
                        ))}
                    </Card>
                </Col>
            </Row>
        </div>
        <CallToAction />
        <Footer />
        </>
    );
}
